import React, { FC, useCallback } from 'react';
import { Formik } from 'formik';
import { useParams } from 'react-router-dom';
import cx from 'classnames';

import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { TicketNotesSelector } from '../../../../store/tickets/selectors';
import { ThunkCreateTicketNote } from '../../../../store/tickets/thunks';
import { UserDataSelector } from '../../../../store/user/selector';

import { ISplitByDate, splitDataByDate } from '../../../../utils/converters';
import { getFormattedDate } from '../../../../utils/dates';
import { ITicketNote } from '../../../../services/tickets/types';

import Input from '../../../../components/Input/Input';
import Button from '../../../../components/Button/Button';
import TicketTimelineItem from './components/TicketTimelineItem';

import { sendNoteInitialValues, ISendNoteInitialValues } from '../../form';

import useStyles from '../../styles';
import { tt } from '@i18n/tt';

interface IProps {
  hideNoteInput?: boolean;
}

const TicketTimeline: FC<IProps> = ({ hideNoteInput = false }) => {
  const notes = useAppSelector(TicketNotesSelector);
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const { contactName } = useAppSelector(UserDataSelector);
  const { ticketId } = useParams();

  const byDate = splitDataByDate<ITicketNote>(notes, true) as Array<ISplitByDate<ITicketNote>>;

  const handleSendNote = useCallback(
    (values: ISendNoteInitialValues) => {
      if (ticketId) {
        dispatch(
          ThunkCreateTicketNote({
            ticketId,
            payload: {
              ...values,
              contactName,
            },
          }),
        );
      }
    },
    [ticketId, dispatch, contactName],
  );

  return (
    <div className={cx(classes.ticketTimeline, { [classes.ticketTimelineNoInput]: hideNoteInput })}>
      <div className={classes.ticketTimelineList}>
        {byDate.map(i => (
          <div key={i.date} className={classes.ticketTimelineItem}>
            <div className={classes.ticketInfoItemDate}>
              {getFormattedDate(i.date, 'MMM DD, YYYY')}
              <div className={classes.divider}>
                <div className={classes.outerCircle}>
                  <div className={classes.innerCircle} />
                </div>
                <div className={classes.line} />
              </div>
            </div>
            <div className={classes.ticketTimelineItemList}>
              {i.data
                // sorting inside date array to have new note on top of list
                .sort((a, b) => (a.dateCreated < b.dateCreated ? 1 : -1))
                .map((note: ITicketNote) => (
                  <TicketTimelineItem note={note} key={note.id} />
                ))}
            </div>
          </div>
        ))}
      </div>
      {!hideNoteInput && (
        <div>
          <Formik
            initialValues={sendNoteInitialValues}
            onSubmit={(values, formikHelpers) => {
              handleSendNote(values);
              formikHelpers.resetForm();
            }}
          >
            {formik => (
              <form onSubmit={formik.handleSubmit} className={classes.form}>
                <Input label={tt('pages.ticket.reply')} name="note" formik={formik} />
                <Button text={tt('pages.ticket.reply')} type="submit" />
              </form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default TicketTimeline;
